// ComplianceAuditTab — Evidence-pack access log, best-execution records,
// override register, full audit trail. Read-only for compliance/auditor roles.

import React, { useState } from 'react';
import { Search, Eye, Shield, FileText, Download } from 'lucide-react';
import { getStatusStyle } from '../../config/statusColors';

const formatDateTime = (d) => d ? new Date(d).toLocaleString('en-GB', {
  day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit'
}) : '—';

const getStatusBadge = (status) => {
  const style = getStatusStyle(status);
  return {
    className: 'inline-flex items-center px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider border rounded-none',
    style: { ...style, borderColor: style.color },
  };
};

const ACTION_CATEGORIES = {
  quote_submitted: 'Quote',
  quote_amended: 'Quote',
  quote_withdrawn: 'Quote',
  quote_accepted: 'Quote',
  rfq_received: 'RFQ',
  rfq_declined: 'RFQ',
  trade_booked: 'Trade',
  settlement_confirmed: 'Settlement',
  evidence_pack_accessed: 'Compliance',
  auto_rule_updated: 'Config',
  limit_override_requested: 'Risk',
  limit_updated: 'Risk',
  counterparty_kyc_change: 'KYC',
  counterparty_suspended: 'KYC',
  session_started: 'Session',
  late_funds_flagged: 'Settlement',
  ssi_change_detected: 'Settlement',
  cb_reference_published: 'Market',
};

const ComplianceAuditTab = ({ auditLog = [], quotes = [], onGetEvidencePack }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  // Get unique categories from audit log
  const categories = [...new Set(auditLog.map(a => ACTION_CATEGORIES[a.action] || 'Other'))].sort();

  const filteredLog = auditLog.filter(entry => {
    const category = ACTION_CATEGORIES[entry.action] || 'Other';
    if (categoryFilter !== 'all' && category !== categoryFilter) return false;
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      entry.action?.toLowerCase().includes(q) ||
      entry.user?.toLowerCase().includes(q) ||
      entry.details?.toLowerCase().includes(q) ||
      entry.entityId?.toLowerCase().includes(q)
    );
  });

  // Evidence pack access entries
  const evidenceAccesses = auditLog.filter(a => a.action === 'evidence_pack_accessed');

  // Override requests
  const overrides = auditLog.filter(a => a.action === 'limit_override_requested' || a.action === 'limit_updated');

  // Best execution records (quotes that were accepted)
  const bestExecRecords = quotes.filter(q => q.status === 'accepted');

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="bg-white border border-zinc-200 rounded-none p-4">
          <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-1">Total Events</p>
          <p className="text-2xl font-bold text-black">{auditLog.length}</p>
        </div>
        <div className="bg-white border border-zinc-200 rounded-none p-4">
          <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-1">Evidence Accesses</p>
          <p className="text-2xl font-bold text-black">{evidenceAccesses.length}</p>
        </div>
        <div className="bg-white border border-zinc-200 rounded-none p-4">
          <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-1">Overrides</p>
          <p className="text-2xl font-bold text-black">{overrides.length}</p>
        </div>
        <div className="bg-white border border-zinc-200 rounded-none p-4">
          <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-1">Best-Exec Records</p>
          <p className="text-2xl font-bold text-black">{bestExecRecords.length}</p>
        </div>
      </div>

      {/* Best Execution Records */}
      {bestExecRecords.length > 0 && (
        <div className="bg-white border border-zinc-200 rounded-none">
          <div className="p-3 border-b border-zinc-100 flex items-center gap-2">
            <Shield className="w-4 h-4 text-zinc-400" />
            <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Best Execution Records</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-200">
                  <th className="text-left py-2 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Quote</th>
                  <th className="text-left py-2 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">RFQ</th>
                  <th className="text-left py-2 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Pair</th>
                  <th className="text-right py-2 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Mid</th>
                  <th className="text-right py-2 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Client Rate</th>
                  <th className="text-right py-2 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Margin</th>
                  <th className="text-left py-2 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Dealer</th>
                  <th className="text-left py-2 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Submitted</th>
                </tr>
              </thead>
              <tbody>
                {bestExecRecords.map(q => (
                  <tr key={q.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                    <td className="py-2 px-3 font-mono text-xs">{q.id}</td>
                    <td className="py-2 px-3 font-mono text-xs">{q.rfqId}</td>
                    <td className="py-2 px-3 font-mono text-xs">{q.currencyPair}</td>
                    <td className="py-2 px-3 text-right font-mono text-xs">{q.midRate?.toFixed(4)}</td>
                    <td className="py-2 px-3 text-right font-mono text-xs font-medium">{q.clientRate?.toFixed(4)}</td>
                    <td className="py-2 px-3 text-right font-mono text-xs">{q.margin}</td>
                    <td className="py-2 px-3 text-xs text-zinc-600">{q.dealerName}</td>
                    <td className="py-2 px-3 text-xs text-zinc-500">{formatDateTime(q.submittedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Full Audit Trail */}
      <div className="bg-white border border-zinc-200 rounded-none">
        <div className="p-3 border-b border-zinc-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Eye className="w-4 h-4 text-zinc-400" />
            <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Full Audit Trail</span>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="border border-zinc-200 rounded-none px-2 py-1 text-xs font-mono focus:outline-none"
            >
              <option value="all">All Categories</option>
              {categories.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-4 py-1.5 border border-zinc-200 rounded-none text-xs focus:outline-none focus:border-zinc-400"
              />
            </div>
          </div>
        </div>
        <div className="overflow-x-auto max-h-96 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white">
              <tr className="border-b border-zinc-200">
                <th className="text-left py-2 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Timestamp</th>
                <th className="text-left py-2 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Category</th>
                <th className="text-left py-2 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Action</th>
                <th className="text-left py-2 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">User</th>
                <th className="text-left py-2 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Entity</th>
                <th className="text-left py-2 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Details</th>
              </tr>
            </thead>
            <tbody>
              {filteredLog.map((entry) => {
                const category = ACTION_CATEGORIES[entry.action] || 'Other';
                return (
                  <tr key={entry.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                    <td className="py-2 px-3 font-mono text-[10px] text-zinc-500 whitespace-nowrap">{formatDateTime(entry.timestamp)}</td>
                    <td className="py-2 px-3">
                      <span className="px-1.5 py-0.5 text-[9px] font-mono uppercase bg-zinc-100 text-zinc-600 rounded-none">
                        {category}
                      </span>
                    </td>
                    <td className="py-2 px-3 font-mono text-[10px] uppercase tracking-wider">{entry.action?.replace(/_/g, ' ')}</td>
                    <td className="py-2 px-3 text-xs text-zinc-600">{entry.user}</td>
                    <td className="py-2 px-3 font-mono text-[10px] text-zinc-500">{entry.entityId || '—'}</td>
                    <td className="py-2 px-3 text-xs text-zinc-600 max-w-xs truncate">{entry.details}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filteredLog.length === 0 && (
            <div className="text-center py-8 text-zinc-400 text-xs">
              <Eye className="w-6 h-6 mx-auto mb-2" />
              No audit entries match your filters
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ComplianceAuditTab;
